// Der WebSocket-Client des Online-Modus.
//
// Er meldet die eigene Position mit fester Rate und reicht alles, was
// hereinkommt, an genau einen Rueckruf weiter. **Er interpretiert nichts** —
// die Deutung liegt in der Szene, die auch die Welt kennt.
//
// Verbindungsabbruch ist der Normalfall, nicht die Ausnahme: das Spiel laeuft
// im Solo-Modus weiter und versucht es mit wachsendem Abstand erneut. Ein
// Inkrementalspiel, das bei WLAN-Wechsel stehen bleibt, ist kaputt.

import type { ClientMessage, ServerMessage } from './protocol';

const SEND_INTERVAL_MS = 100;
const RETRY_STEPS_MS = [1000, 2000, 4000, 8000, 15000];

export interface SocketHooks {
  onMessage(message: ServerMessage): void;
  onOpen(): void;
  onClose(): void;
}

export interface ShipReport {
  x: number;
  y: number;
  vx: number;
  vy: number;
  angle: number;
  thrust: number;
}

export class FieldSocket {
  private socket: WebSocket | null = null;
  private sendTimer: ReturnType<typeof setInterval> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retries = 0;
  private closedByUs = false;
  private report: ShipReport = { x: 0, y: 0, vx: 0, vy: 0, angle: 0, thrust: 0 };

  constructor(
    private readonly url: () => string,
    private readonly hooks: SocketHooks,
  ) {}

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  connect(): void {
    this.closedByUs = false;
    this.open();
  }

  disconnect(): void {
    this.closedByUs = true;
    this.stopTimers();
    this.socket?.close();
    this.socket = null;
  }

  /** Die eigene Position setzen. Gesendet wird getaktet, nicht je Frame. */
  update(report: ShipReport): void {
    this.report = report;
  }

  /**
   * Ein Signal an die Mitspieler setzen.
   *
   * Traegt bewusst nur den Typ: den Ort nimmt der Server aus der zuletzt
   * angenommenen Meldung, den Absender aus der Verbindung (FG-32). Den Takt
   * begrenzt der Server; zu schnelle Signale verwirft er still.
   */
  sendSignal(): void {
    this.send({ t: 'signal' });
  }

  private open(): void {
    let socket: WebSocket;
    try {
      socket = new WebSocket(this.url());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.retries = 0;
      this.hooks.onOpen();
      this.sendState();
      this.sendTimer = setInterval(() => this.sendState(), SEND_INTERVAL_MS);
    };

    socket.onmessage = (event) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(String(event.data)) as ServerMessage;
      } catch {
        return;                        // eine unlesbare Nachricht ist kein Absturz
      }
      this.hooks.onMessage(message);
    };

    socket.onclose = () => {
      this.stopTimers();
      this.socket = null;
      this.hooks.onClose();
      if (!this.closedByUs) this.scheduleRetry();
    };

    socket.onerror = () => {
      // `onclose` folgt immer — dort steht der Wiederversuch.
    };
  }

  private sendState(): void {
    this.send({
      t: 'state',
      x: this.report.x,
      y: this.report.y,
      vx: this.report.vx,
      vy: this.report.vy,
      a: this.report.angle,
      th: this.report.thrust,
    });
  }

  send(message: ClientMessage): void {
    if (!this.connected) return;
    this.socket?.send(JSON.stringify(message));
  }

  private scheduleRetry(): void {
    const wait = RETRY_STEPS_MS[Math.min(this.retries, RETRY_STEPS_MS.length - 1)];
    this.retries++;
    this.retryTimer = setTimeout(() => this.open(), wait);
  }

  private stopTimers(): void {
    if (this.sendTimer !== null) { clearInterval(this.sendTimer); this.sendTimer = null; }
    if (this.retryTimer !== null) { clearTimeout(this.retryTimer); this.retryTimer = null; }
  }
}
